
import React, { useState } from "react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AdminCompanyDialog } from "@/components/admin/AdminCompanyDialog";
import { AdminCompanyDetailsDialog } from "@/components/admin/AdminCompanyDetailsDialog";
import { AdminCompanyEditDialog } from "@/components/admin/AdminCompanyEditDialog";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Eye } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Company = {
  id: number;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  area_atuacao: string;
  ativo?: boolean | null;
  endereco?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  created_at: string;
  company_subscriptions?: Array<{
    id: number;
    status: string;
    started_at: string;
    expires_at?: string;
    subscription_plans?: {
      name: string;
      price: number;
      max_users?: number | null;
      max_services?: number | null;
      max_appointments?: number | null;
    };
  }>;
};

export default function AdminCompanies() {
  const { isLoading: authLoading, isAdmin } = useAdminAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: companies, isLoading, error, refetch } = useQuery<Company[]>({
    queryKey: ['admin-companies', searchTerm],
    queryFn: async () => {
      console.log('🔍 AdminCompanies: Iniciando query de empresas...');
      
      let query = supabase
        .from('empresas')
        .select(`
          *,
          company_subscriptions(
            id,
            status,
            started_at,
            expires_at,
            subscription_plans(
              name,
              price,
              max_users,
              max_services,
              max_appointments
            )
          )
        `);

      if (searchTerm) {
        query = query.or(`razao_social.ilike.%${searchTerm}%,nome_fantasia.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('❌ AdminCompanies: Erro ao buscar empresas:', error);
        throw error;
      }

      console.log('✅ AdminCompanies: Empresas carregadas:', data?.length || 0);
      return (data || []) as Company[];
    },
    enabled: !authLoading && isAdmin, // Só executar query quando auth estiver pronta
    retry: 1,
    retryDelay: 1000,
    staleTime: 30000,
  });

  const getOperationalStatus = (company: Company) => {
    if (company.ativo === false) {
      return { label: "Inativa", color: "bg-red-100 text-red-800" };
    }

    return { label: "Ativa", color: "bg-green-100 text-green-800" };
  };

  const getSubscriptionInfo = (subscriptions: Company["company_subscriptions"]) => {
    if (!subscriptions || subscriptions.length === 0) {
      return { label: "Sem assinatura", color: "bg-gray-100 text-gray-800" };
    }

    const activeSubscription = subscriptions.find((sub) => sub.status === "active");
    if (activeSubscription) {
      return { label: "Assinatura ativa", color: "bg-green-100 text-green-800" };
    }

    return { label: "Assinatura inativa", color: "bg-amber-100 text-amber-800" };
  };

  const handleView = (company: Company) => {
    setSelectedCompany(company);
    setViewDialogOpen(true);
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setEditDialogOpen(true);
  };

  const handleEditFromDetails = (company: Company) => {
    setSelectedCompany(company);
    setEditDialogOpen(true);
  };

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-2xl">
          <h2 className="text-destructive font-semibold mb-2">Erro ao carregar empresas</h2>
          <p className="text-destructive/80 text-sm mb-4">
            {error instanceof Error ? error.message : 'Erro desconhecido ao buscar dados'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            Tentar Novamente
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Empresas"
          subtitle="Gerencie as empresas cadastradas na plataforma."
          backTo="/admin/control-center"
          rightTop={
            <Button className="gap-2" onClick={() => setDialogOpen(true)}>
              <Plus size={16} />
              Nova Empresa
            </Button>
          }
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar empresas...",
          }}
        />

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <div className="text-muted-foreground">Carregando empresas...</div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Data Cadastro</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies?.map((company) => {
                    const companyStatus = getOperationalStatus(company);
                    const subscriptionInfo = getSubscriptionInfo(company.company_subscriptions);
                    const activePlan = company.company_subscriptions?.find(sub => sub.status === 'active');
                    
                    return (
                      <TableRow key={company.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{company.razao_social || 'Não informado'}</div>
                            {company.nome_fantasia && (
                              <div className="text-sm text-gray-500">{company.nome_fantasia}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{company.cnpj || 'Não informado'}</TableCell>
                        <TableCell>
                          <Badge className={companyStatus.color}>{companyStatus.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm font-medium">
                              {activePlan?.subscription_plans?.name || 'Nenhum plano'}
                            </p>
                            <Badge className={subscriptionInfo.color}>{subscriptionInfo.label}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(company.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleView(company)}
                            >
                              <Eye size={16} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEdit(company)}
                            >
                              <Edit size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <AdminCompanyDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      
      <AdminCompanyDetailsDialog 
        company={selectedCompany}
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        onEdit={handleEditFromDetails}
      />
      
      <AdminCompanyEditDialog
        company={selectedCompany}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </AdminLayout>
  );
}
